import { Edit, Trash } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { BackButton } from "@/components/ui/BackButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface ListDetailHeaderProps {
  listName: string;
  listId: string;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

export const ListDetailHeader: React.FC<ListDetailHeaderProps> = ({
  listName,
  listId,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onDelete
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex justify-between items-center mb-6">
      <BackButton 
        to="/lists" 
        label={t("lists.detail.backToLists", "Back to Lists")} 
      />
      <div className="flex gap-2 pt-2">
        <FavoriteButton
          entityType="list"
          entityId={listId}
          entityName={listName}
          variant="outline"
          size="icon"
        />
        {!isEditing ? (
          <Button 
            variant="outline"
            size="icon"
            className="flex items-center justify-center"
            onClick={onEdit}
            title={t('lists.actions.edit', 'Edit')}
          >
            <Edit className="h-4 w-4" />
          </Button>
        ) : null}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button 
              variant="outline" 
              size="icon"
              className="flex items-center justify-center text-red-600 hover:text-red-700"
              title={t("lists.actions.delete", "Delete")}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("lists.detail.deleteConfirmTitle", "Delete List")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("lists.detail.deleteConfirmDescription", "Are you sure you want to delete this list? This action cannot be undone.")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={onDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                {t("common.delete", "Delete")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
